import { TemplateResult, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import innerStyles from './outline-kyruus-checkbox-facet.inner.pcss?inline';
import globalStyles from './outline-kyruus-checkbox-facet.global.pcss?inline';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';
import '../../outline-icon/outline-icon';

/**
 * The Kyruus Checkbox Facet Component
 * @element outline-kyruus-checkbox-facet
 *
 * @property {Boolean} showAll - Tracks if all items are being shown
 * @property {Boolean} filterList - Provide a filter to narrow down large lists
 *
 * @slot facet-label - Slot to add a label for the facet
 * @slot facet-list - Slot to add the list of checkboxes
 *
 * @dependency outline-cta
 * @dependency outline-icon
 *
 */

@customElement('outline-kyruus-checkbox-facet')
export class OutlineKyruusCheckboxFacet extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  @property({ type: Boolean, reflect: true }) showAll = false;
  @property({ type: Boolean, reflect: true }) filterList = false;

  toggleShowAll() {
    this.showAll = !this.showAll;
  }

  render(): TemplateResult {
    const wrapperClasses = {
      'facet-wrapper': true,
      'expanded': this.showAll,
    };
    return html`
      <div class="${classMap(wrapperClasses)}">
        <div class="facet-top">
          <slot name="facet-label"></slot>
          <slot name="facet-reset"></slot>
        </div>
        ${this.renderListFunctionality()}
      </div>
    `;
  }

  /**
   * Provides the list section of the facet.
   *
   * Depending on the scale of the list, we want to render differently.
   * There are three possibilities:
   * 1) 5 or fewer list items, we want to render all of them.
   * 2) 15 or fewer list items, we want to render 5 and provide a "show more"
   *    button to expand and show the rest.
   * 3) 16 and up we want a mini search functionality to filter options.
   * @returns HTML
   */
  renderListFunctionality() {
    // How many list items do we have?
    const listItems = this.querySelectorAll('li');
    const listCount = listItems.length;

    // Search widget
    if (listCount > 15) {
      this.showAll = true;
      this.filterList = true;

      // Do some searchy type stuff and things.
      const handleSearchInput = (event: Event) => {
        const input = event.target as HTMLInputElement;
        const searchTerm = input.value.toLowerCase();
        listItems.forEach(item => {
          const label = item.querySelector('label');
          if (label?.textContent?.toLowerCase().includes(searchTerm)) {
            item.style.display = '';
          } else {
            item.style.display = 'none';
          }
        });
      };

      // Render it.
      return html`
        <input
          type="text"
          @input="${handleSearchInput}"
          placeholder="Search..."
        />
        <slot name="facet-list"></slot>
      `;
    }

    // Show more/show less
    if (listCount > 5) {
      // Collapsed list, expand button.
      if (!this.showAll) {
        return html`
          <slot name="facet-list"></slot>
          <button
            class="outline-cta outline-cta--medium outline-cta--button-primary"
            @click="${this.toggleShowAll}"
          >
            Show more
            <outline-icon
              library="legacy"
              size="1.5rem"
              name="plus"
            ></outline-icon>
          </button>
        `;
      }
      // Expanded list, collapse button.
      return html` <slot name="facet-list"></slot>
        <button
          class="outline-cta outline-cta--medium outline-cta--button-primary"
          @click="${this.toggleShowAll}"
        >
          Show less
          <outline-icon
            library="legacy"
            size="1.5rem"
            name="minus"
          ></outline-icon>
        </button>`;
    }

    // Just the list
    return html` <slot name="facet-list"></slot> `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'outline-kyruus-checkbox-facet': OutlineKyruusCheckboxFacet;
  }
}
